import {
  useState, useRef, useCallback, useEffect, useMemo,
} from 'react';
// import { message } from 'antd';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import { useTranslation } from 'react-i18next';
import localeData from 'dayjs/plugin/localeData';

import alarmsApi from '@api/alarmsApi';
import requestHelpers from '@utils/helpers/forRequest';

import getColumns from './getColumns';

dayjs.extend(weekday);
dayjs.extend(localeData);

function useAlarmas_Notifications() {

  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState(null);

  const defaultRangeValue = useMemo(() => [dayjs().subtract(30, 'days'), dayjs()], []);

  const columns = useMemo(() => getColumns({ t }), [t]);

  const abortController = useRef(null);
  const paginationRef = useRef({ current: 1, pageSize: 20 });
  const tableFiltersRef = useRef({});

  const othersFiltersRef = useRef(
    (() => {

      const [start_date, end_date] = defaultRangeValue.map(requestHelpers.getDateFormatForRequest);
      return { start_date, end_date };

    })(),
  );

  const makeRequest = useCallback(() => {

    const { current, pageSize: limit } = paginationRef.current;
    const offset = (current - 1) * limit;
    const filters = { ...tableFiltersRef.current, ...othersFiltersRef.current };

    if (abortController.current) abortController.current.abort();
    const newAbortController = new AbortController();
    const { signal } = newAbortController;
    abortController.current = newAbortController;

    setLoading(true);
    alarmsApi
      .getAlarms({
        signal, offset, limit, ...filters,
      })
      .then((response) => {

        const resp = {
          count: response.lenght,
          results: response,
        };
        const { count: total, results } = resp;
        paginationRef.current = { ...paginationRef.current, total };

        setDataSource(results);
        setLoading(false);

      })
      .catch((resError) => {

        if (signal.aborted) return;
        const { fem } = resError;
        // eslint-disable-next-line no-console
        console.log(t(fem));

      });

    setLoading(true);

  }, [t]);

  const onChangeRangeDate = useCallback((dates) => {

    const [start_date, end_date] = dates
      ? dates.map(requestHelpers.getDateFormatForRequest)
      : [undefined, undefined];

    othersFiltersRef.current = { ...othersFiltersRef.current, start_date, end_date };
    paginationRef.current = { ...paginationRef.current, current: 1 };
    if (start_date && end_date) makeRequest();

    // eslint-disable-next-line
  }, []);

  const onSearch = useCallback((device) => {

    if (!device) {

      const newOthersFiltersRef = { ...othersFiltersRef.current };
      if (newOthersFiltersRef?.search) delete newOthersFiltersRef.search;
      othersFiltersRef.current = newOthersFiltersRef;

    } else {

      othersFiltersRef.current = { ...othersFiltersRef.current, search: device };

    }

    paginationRef.current = { ...paginationRef.current, current: 1 };
    makeRequest();
    // eslint-disable-next-line
  }, []);

  const handleTableChange = useCallback((pagination, filters) => {

    const { pageSize: newLimit } = pagination;
    const { pageSize: currentLimit } = paginationRef.current;

    if (currentLimit !== newLimit) setDataSource(null);

    const mapFilters = Object.entries(filters).reduce((acc, cur) => {

      const [key, value] = cur;
      if (!value || !Array.isArray(value)) return acc;

      acc[key] = value.join(',');
      return acc;

    }, {});

    tableFiltersRef.current = mapFilters;
    paginationRef.current = { ...paginationRef.current, ...pagination };

    makeRequest();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {

    makeRequest();
    // eslint-disable-next-line
  }, []);

  return {
    pagination: paginationRef.current,
    loading,
    columns,
    dataSource,
    handleTableChange,
    onChangeRangeDate,
    onSearch,
    defaultRangeValue,
  };

}

export default useAlarmas_Notifications;
